import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Lock, Mail, Phone, Loader2, ArrowRight, Smartphone, ChevronDown, CheckCircle } from 'lucide-react';
import ImageCarousel from '../components/ImageCarousel';

interface CountryCode {
  code: string;
  dialCode: string;
  flag: string;
  name: string;
}

const countryCodes: CountryCode[] = [
  { code: 'SN', dialCode: '+221', flag: 'SN', name: 'Sénégal' },
  { code: 'FR', dialCode: '+33', flag: 'FR', name: 'France' },
  { code: 'US', dialCode: '+1', flag: 'US', name: 'États-Unis' },
  { code: 'CI', dialCode: '+225', flag: 'CI', name: 'Côte d\'Ivoire' },
  { code: 'CM', dialCode: '+237', flag: 'CM', name: 'Cameroun' },
  { code: 'ML', dialCode: '+223', flag: 'ML', name: 'Mali' },
  { code: 'BF', dialCode: '+226', flag: 'BF', name: 'Burkina Faso' },
  { code: 'DZ', dialCode: '+213', flag: 'DZ', name: 'Algérie' },
  { code: 'MA', dialCode: '+212', flag: 'MA', name: 'Maroc' },
  { code: 'TN', dialCode: '+216', flag: 'TN', name: 'Tunisie' },
];

// Fonction pour obtenir un exemple de numéro selon le pays
const getPhonePlaceholder = (countryCode: string): string => {
  const placeholders: Record<string, string> = {
    'SN': '77 123 45 67',
    'FR': '6 12 34 56 78',
    'US': '(555) 123-4567',
    'CI': '07 12 34 56 78',
    'CM': '6 12 34 56 78',
    'ML': '76 12 34 56',
    'BF': '70 12 34 56',
    'DZ': '5 12 34 56 78',
    'MA': '6 12 34 56 78',
    'TN': '9 12 34 56 78',
  };
  return placeholders[countryCode] || '123 456 789';
};

function LoginPage() {
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone' | 'others' | 'whatsapp'>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(countryCodes[0]);
  const [whatsappCountry, setWhatsappCountry] = useState<CountryCode>(countryCodes[0]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showWhatsappDropdown, setShowWhatsappDropdown] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [whatsappOtpCode, setWhatsappOtpCode] = useState(['', '', '', '', '', '']);
  const [showOtp, setShowOtp] = useState(false);
  const [showWhatsappOtp, setShowWhatsappOtp] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Images du carousel en ordre spécifié
  const carouselImages = [
    '/assets/images/BurgersBusiness.gif',
    '/assets/images/dee_blvckon IG.jpeg',
    '/assets/images/plusone_usa.jpeg',
    '/assets/images/free2.png',
    '/assets/images/télécharger (47).jpeg',
    '/assets/images/Lorvae.jpeg',
  ];

  // Vérifier si l'utilisateur est déjà connecté au chargement de la page
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Rediriger vers la page d'accueil si déjà connecté
          const redirect = searchParams.get('redirect') || '/';
          navigate(redirect);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de session:', error);
        setLoading(false);
      }
    };

    checkSession();

    // Écouter les changements de session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session) {
        const redirect = searchParams.get('redirect') || '/';
        navigate(redirect);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, searchParams]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (response.error) {
        setError(response.error.message || 'Erreur de connexion');
        setLoading(false);
        return;
      }

      if (response.data.user) {
        // Connexion réussie, rediriger
        const redirect = searchParams.get('redirect') || '/';
        navigate(redirect);
      }
    } catch {
      setError('Une erreur inattendue est survenue');
      setLoading(false);
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone) {
      setError('Veuillez entrer votre numéro de téléphone');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fullPhoneNumber = `${selectedCountry.dialCode}${phone.replace(/\s/g, '')}`;
      const { error: otpError } = await supabase.auth.signInWithOtp({
        phone: fullPhoneNumber,
      });

      if (otpError) {
        setError(otpError.message || 'Erreur lors de l\'envoi du code');
        setLoading(false);
        return;
      }

      setShowOtp(true);
      setLoading(false);
    } catch {
      setError('Une erreur inattendue est survenue');
      setLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    if (value && !/^\d$/.test(value)) return;
    
    const newOtpCode = [...otpCode];
    newOtpCode[index] = value;
    setOtpCode(newOtpCode);
    setError(null);

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }

    if (newOtpCode.every(code => code !== '') && newOtpCode.length === 6) {
      handleOtpVerification(newOtpCode.join(''));
    }
  };

  const handleOtpVerification = async (codeValue?: string) => {
    const code = codeValue || otpCode.join('');
    
    if (code.length !== 6) {
      setError('Veuillez saisir les 6 chiffres du code de vérification');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fullPhoneNumber = `${selectedCountry.dialCode}${phone.replace(/\s/g, '')}`;
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        phone: fullPhoneNumber,
        token: code,
        type: 'sms',
      });

      if (verifyError) {
        setError(verifyError.message || 'Code incorrect. Veuillez réessayer.');
        setOtpCode(['', '', '', '', '', '']);
        setLoading(false);
        setTimeout(() => {
          const firstInput = document.getElementById('otp-0');
          firstInput?.focus();
        }, 100);
        return;
      }

      if (data.session) {
        const redirect = searchParams.get('redirect') || '/';
        navigate(redirect);
      }
    } catch {
      setError('Une erreur inattendue est survenue');
      setLoading(false);
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleWhatsappSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!whatsappPhone) {
      setError('Veuillez entrer votre numéro WhatsApp');
      return;
    }
    setShowWhatsappOtp(true);
    setLoading(false);
  };

  const handleWhatsappOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const newOtpCode = [...whatsappOtpCode];
    newOtpCode[index] = value;
    setWhatsappOtpCode(newOtpCode);

    if (value && index < 5) {
      const nextInput = document.getElementById(`whatsapp-otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleWhatsappOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !whatsappOtpCode[index] && index > 0) {
      const prevInput = document.getElementById(`whatsapp-otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  useEffect(() => {
    if (showOtp) {
      setTimeout(() => {
        const firstInput = document.getElementById('otp-0');
        firstInput?.focus();
      }, 100);
    }
  }, [showOtp]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-country-dropdown]')) {
        setShowCountryDropdown(false);
      }
      if (!target.closest('[data-whatsapp-dropdown]')) {
        setShowWhatsappDropdown(false);
      }
    };

    if (showCountryDropdown || showWhatsappDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCountryDropdown, showWhatsappDropdown]);


  if (loading && !showOtp && !showWhatsappOtp) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#ff0442] via-[#ff1a4d] to-[#ff0442] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#ff0442] via-[#ff1a4d] to-[#ff0442] flex items-center justify-center p-4 md:p-2 overflow-x-hidden">
      <div className="w-full max-w-6xl overflow-x-hidden">
        <div className="grid gap-8 md:gap-6 md:grid-cols-[1.15fr_1fr]">
          {/* Carousel Section - Hidden on mobile, visible on desktop */}
          <div className="hidden md:flex md:flex-col md:items-center md:space-y-4 md:order-1">
            <div className="w-full">
              <ImageCarousel />
            </div>
            {/* Carousel d'images - Desktop uniquement */}
            <div className="hidden md:block w-full overflow-x-hidden mt-4">
              <div className="relative w-full overflow-hidden">
                <div className="flex gap-3 animate-scroll-left">
                  {/* Dupliquer les images pour un défilement infini continu */}
                  {[...carouselImages, ...carouselImages].map((image, index) => (
                    <div
                      key={index}
                      className="shrink-0 aspect-square rounded-xl overflow-hidden border-2 border-white shadow-lg"
                      style={{ width: 'calc((100% - 1rem) / 3)', minWidth: 'calc((100% - 1rem) / 3)' }}
                    >
                      <img
                        src={image}
                        alt={`Promotion ${(index % carouselImages.length) + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          console.error('Error loading carousel image:', image);
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Login Section */}
          <div className="p-8 bg-white rounded-2xl shadow-2xl order-1 md:order-2 overflow-x-hidden">
            {/* ShopLink Logo */}
            <div className="flex justify-center mb-6">
              <img
                src="/assets/images/Shoplinkpng.png"
                alt="ShopLink"
                className="h-20"
              />
            </div>

            {/* Carousel d'images - Mobile uniquement */}
            <div className="mb-6 md:hidden w-full overflow-x-hidden">
              <div className="relative w-full overflow-hidden">
                <div className="flex gap-3 animate-scroll-left py-2">
                  {/* Dupliquer les images pour un défilement infini continu */}
                  {[...carouselImages, ...carouselImages].map((image, index) => (
                    <div
                      key={index}
                      className="shrink-0 w-24 h-24 rounded-lg overflow-hidden border-2 border-[#ff0442] shadow-sm"
                    >
                      <img
                        src={image}
                        alt={`Promotion ${(index % carouselImages.length) + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          console.error('Error loading carousel image:', image);
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <h1 className="mb-2 text-3xl font-bold text-center text-gray-900">
              Connectez-vous à Burok
            </h1>
            <p className="mb-8 text-center text-gray-600">
              Accédez à votre compte et faites vos achats facilement
            </p>

            {/* Toggle Method */}
            <div className="flex gap-1 p-1 mb-6 bg-gray-100 rounded-xl">
              <button
                onClick={() => setLoginMethod('email')}
                className={`flex-1 py-3 px-2 rounded-lg font-semibold transition-all duration-300 ${
                  loginMethod === 'email'
                    ? 'bg-gradient-to-r from-[#ff0442] to-[#ff1a4d] text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Mail className="inline-block mr-1 w-5 h-5 md:mr-2" />
                <span className="hidden sm:inline">Email</span>
              </button>
              <button
                onClick={() => setLoginMethod('phone')}
                className={`flex-1 py-3 px-2 rounded-lg font-semibold transition-all duration-300 ${
                  loginMethod === 'phone'
                    ? 'bg-gradient-to-r from-[#ff0442] to-[#ff1a4d] text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Phone className="inline-block mr-1 w-5 h-5 md:mr-2" />
                <span className="hidden sm:inline">Téléphone</span>
              </button>
              <button
                onClick={() => setLoginMethod('others')}
                className={`flex-1 py-3 px-2 rounded-lg font-semibold transition-all duration-300 ${
                  loginMethod === 'others'
                    ? 'bg-gradient-to-r from-[#ff0442] to-[#ff1a4d] text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Smartphone className="inline-block mr-1 w-5 h-5 md:mr-2" />
                <span className="hidden sm:inline">Autres</span>
              </button>
            </div>

            {/* Others Login Methods */}
            {loginMethod === 'others' ? (
              <div className="space-y-3">
                <button
                  onClick={() => setLoginMethod('whatsapp')}
                  className="flex items-center p-4 w-full bg-white rounded-xl border-2 border-gray-200 transition-all duration-300 hover:border-[#ff0442] hover:shadow-lg group"
                >
                  <div className="flex justify-center items-center mr-4 w-12 h-12 bg-white rounded-lg border border-gray-200 transition-transform group-hover:scale-110 overflow-hidden">
                    <img
                      src="/assets/images/whatsapplogo.png"
                      alt="WhatsApp"
                      className="object-contain w-full h-full p-1"
                      onError={(e) => {
                        console.error('Error loading WhatsApp logo');
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                  <span className="font-semibold text-gray-900">Continuer avec WhatsApp</span>
                </button>

                <button
                  onClick={() => console.log('Google login')}
                  className="flex items-center p-4 w-full bg-white rounded-xl border-2 border-gray-200 transition-all duration-300 hover:border-[#ff0442] hover:shadow-lg group"
                >
                  <div className="flex justify-center items-center mr-4 w-12 h-12 bg-white rounded-lg border border-gray-200 transition-transform group-hover:scale-110 overflow-hidden">
                    <img
                      src="/assets/images/Google-Symbol.png"
                      alt="Google"
                      className="object-contain w-full h-full p-1"
                      onError={(e) => {
                        console.error('Error loading Google logo');
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                  <span className="font-semibold text-gray-900">Continuer avec Google</span>
                </button>
              </div>
            ) : loginMethod === 'phone' ? (
              <form onSubmit={handlePhoneSubmit} className="space-y-4">
                {!showOtp ? (
                  <>
                    <div className="flex gap-2" data-country-dropdown>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                          className="flex justify-center items-center px-3 h-12 md:h-14 bg-gray-50 rounded-xl border-2 border-gray-200 transition-all duration-300 hover:border-[#ff0442]"
                        >
                          <img
                            src={`https://flagcdn.com/w40/${selectedCountry.flag.toLowerCase()}.png`}
                            alt={selectedCountry.name}
                            className="object-cover w-8 h-6 rounded"
                          />
                          <ChevronDown className="ml-2 w-4 h-4 text-gray-600" />
                        </button>

                        {showCountryDropdown && (
                          <div className="overflow-y-auto absolute left-0 top-full z-50 mt-2 w-72 max-h-60 bg-white rounded-xl border-2 border-gray-200 shadow-xl">
                            {countryCodes.map((country) => (
                              <button
                                key={country.code}
                                type="button"
                                onClick={() => {
                                  setSelectedCountry(country);
                                  setShowCountryDropdown(false);
                                }}
                                className="flex items-center p-3 w-full transition-colors duration-200 hover:bg-[#ff0442]/10"
                              >
                                <img
                                  src={`https://flagcdn.com/w40/${country.flag.toLowerCase()}.png`}
                                  alt={country.name}
                                  className="object-cover mr-3 w-8 h-6 rounded"
                                />
                                <span className="flex-1 font-medium text-left text-gray-900">{country.name}</span>
                                <span className="text-gray-500">{country.dialCode}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-1 items-center rounded-xl border-2 border-gray-200 transition-all duration-300 focus-within:border-[#ff0442]">
                        <span className="flex items-center px-3 md:px-4 h-12 md:h-14 text-sm md:text-base font-medium text-gray-700 bg-gray-50 rounded-l-xl border-r border-gray-200">
                          {selectedCountry.dialCode}
                        </span>
                        <div className="relative flex-1">
                          <Phone className="absolute left-3 top-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400 transform -translate-y-1/2" />
                          <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder={getPhonePlaceholder(selectedCountry.code)}
                            required
                            className="py-3 md:py-4 pr-3 md:pr-4 pl-10 md:pl-12 w-full text-sm md:text-base rounded-r-xl border-0 focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>

                    {error && (
                      <div className="p-4 text-red-600 bg-red-50 rounded-xl border-2 border-red-200">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-[#ff0442] to-[#ff1a4d] text-white py-4 px-6 rounded-xl font-semibold hover:shadow-lg transform transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          Envoyer le code
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="space-y-4">
                      <p className="text-center text-gray-600">
                        Entrez le code de vérification à 6 chiffres
                      </p>
                      <div className="flex gap-3 justify-center">
                        {otpCode.map((digit, index) => (
                          <input
                            key={index}
                            id={`otp-${index}`}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              handleOtpChange(index, value);
                            }}
                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                            aria-label={`Chiffre ${index + 1} du code OTP`}
                            title={`Chiffre ${index + 1}`}
                            autoFocus={index === 0 && showOtp}
                            className="w-12 h-14 text-2xl font-bold text-center rounded-xl border-2 border-gray-200 transition-all duration-300 focus:outline-none focus:border-[#ff0442]"
                          />
                        ))}
                      </div>
                    </div>

                    {error && (
                      <div className="p-4 text-red-600 bg-red-50 rounded-xl border-2 border-red-200">
                        {error}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => handleOtpVerification()}
                      disabled={loading || otpCode.some(code => code === '')}
                      className="w-full bg-gradient-to-r from-[#ff0442] to-[#ff1a4d] text-white py-4 px-6 rounded-xl font-semibold hover:shadow-lg transform transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                          Vérification en cours...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="mr-2 w-5 h-5" />
                          Vérifier le code
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={async () => {
                        setOtpCode(['', '', '', '', '', '']);
                        setError(null);
                        setLoading(true);
                        try {
                          const fullPhoneNumber = `${selectedCountry.dialCode}${phone.replace(/\s/g, '')}`;
                          const { error: resendError } = await supabase.auth.signInWithOtp({
                            phone: fullPhoneNumber,
                          });
                          
                          if (resendError) {
                            setError(resendError.message || 'Erreur lors du renvoi du code');
                          }
                        } catch {
                          setError('Erreur lors du renvoi du code');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      className="w-full text-sm font-medium text-[#ff0442] hover:text-[#ff1a4d] disabled:opacity-50"
                    >
                      Renvoyer le code
                    </button>
                  </>
                )}
              </form>
            ) : loginMethod === 'whatsapp' ? (
              <form onSubmit={handleWhatsappSubmit} className="space-y-4">
                {!showWhatsappOtp ? (
                  <>
                    <div className="flex gap-2" data-whatsapp-dropdown>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowWhatsappDropdown(!showWhatsappDropdown)}
                          className="flex justify-center items-center px-3 h-12 md:h-14 bg-gray-50 rounded-xl border-2 border-gray-200 transition-all duration-300 hover:border-[#ff0442]"
                        >
                          <img
                            src={`https://flagcdn.com/w40/${whatsappCountry.flag.toLowerCase()}.png`}
                            alt={whatsappCountry.name}
                            className="object-cover w-8 h-6 rounded"
                          />
                          <ChevronDown className="ml-2 w-4 h-4 text-gray-600" />
                        </button>

                        {showWhatsappDropdown && (
                          <div className="overflow-y-auto absolute left-0 top-full z-50 mt-2 w-72 max-h-60 bg-white rounded-xl border-2 border-gray-200 shadow-xl">
                            {countryCodes.map((country) => (
                              <button
                                key={country.code}
                                type="button"
                                onClick={() => {
                                  setWhatsappCountry(country);
                                  setShowWhatsappDropdown(false);
                                }}
                                className="flex items-center p-3 w-full transition-colors duration-200 hover:bg-[#ff0442]/10"
                              >
                                <img
                                  src={`https://flagcdn.com/w40/${country.flag.toLowerCase()}.png`}
                                  alt={country.name}
                                  className="object-cover mr-3 w-8 h-6 rounded"
                                />
                                <span className="flex-1 font-medium text-left text-gray-900">{country.name}</span>
                                <span className="text-gray-500">{country.dialCode}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-1 items-center rounded-xl border-2 border-gray-200 transition-all duration-300 focus-within:border-[#ff0442]">
                        <span className="flex items-center px-3 md:px-4 h-12 md:h-14 text-sm md:text-base font-medium text-gray-700 bg-gray-50 rounded-l-xl border-r border-gray-200">
                          {whatsappCountry.dialCode}
                        </span>
                        <div className="relative flex-1">
                          <img
                            src="/assets/images/whatsapplogo.png"
                            alt="WhatsApp"
                            className="absolute left-3 top-1/2 w-4 h-4 md:w-5 md:h-5 transform -translate-y-1/2 object-contain"
                            onError={(e) => {
                              console.error('Error loading WhatsApp logo in input');
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                          <input
                            type="tel"
                            value={whatsappPhone}
                            onChange={(e) => setWhatsappPhone(e.target.value)}
                            placeholder={getPhonePlaceholder(whatsappCountry.code)}
                            required
                            className="py-3 md:py-4 pr-3 md:pr-4 pl-10 md:pl-12 w-full text-sm md:text-base rounded-r-xl border-0 focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>

                    {error && (
                      <div className="p-4 text-red-600 bg-red-50 rounded-xl border-2 border-red-200">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-[#ff0442] to-[#ff1a4d] text-white py-4 px-6 rounded-xl font-semibold hover:shadow-lg transform transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          Envoyer le code
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="space-y-4">
                      <div className="flex justify-center items-center mb-2">
                        <img
                          src="/assets/images/whatsapplogo.png"
                          alt="WhatsApp"
                          className="mr-3 w-8 h-8 object-contain"
                          onError={(e) => {
                            console.error('Error loading WhatsApp logo in OTP section');
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                        <p className="font-semibold text-center text-gray-600">
                          Code WhatsApp
                        </p>
                      </div>
                      <p className="text-sm text-center text-gray-600">
                        Entrez le code de vérification à 6 chiffres
                      </p>
                      <div className="flex gap-3 justify-center">
                        {whatsappOtpCode.map((digit, index) => (
                          <input
                            key={index}
                            id={`whatsapp-otp-${index}`}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              handleWhatsappOtpChange(index, value);
                            }}
                            onKeyDown={(e) => handleWhatsappOtpKeyDown(index, e)}
                            aria-label={`Chiffre ${index + 1} du code OTP WhatsApp`}
                            title={`Chiffre ${index + 1}`}
                            className="w-12 h-14 text-2xl font-bold text-center rounded-xl border-2 border-gray-200 transition-all duration-300 focus:outline-none focus:border-[#ff0442]"
                          />
                        ))}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setWhatsappOtpCode(['', '', '', '', '', '']);
                        setShowWhatsappOtp(false);
                      }}
                      className="w-full text-sm font-medium text-[#ff0442] hover:text-[#ff1a4d]"
                    >
                      Renvoyer le code
                    </button>
                  </>
                )}
              </form>
            ) : (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    required
                    className="py-4 pr-4 pl-12 w-full rounded-xl border-2 border-gray-200 transition-all duration-300 focus:outline-none focus:border-[#ff0442]"
                  />
                </div>

                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mot de passe"
                    required
                    className="py-4 pr-4 pl-12 w-full rounded-xl border-2 border-gray-200 transition-all duration-300 focus:outline-none focus:border-[#ff0442]"
                  />
                </div>

                {error && (
                  <div className="p-4 text-red-600 bg-red-50 rounded-xl border-2 border-red-200">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#ff0442] to-[#ff1a4d] text-white py-4 px-6 rounded-xl font-semibold hover:shadow-lg transform transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      Connexion en cours...
                    </>
                  ) : (
                    <>
                      Se connecter
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Additional Links */}
            <div className="mt-6 space-y-4 text-center">
              {loginMethod === 'email' && (
                <a href="#" className="block text-sm font-medium text-[#ff0442] hover:text-[#ff1a4d]">
                  Mot de passe oublié ?
                </a>
              )}
              
              {/* Download App Banner */}
              <div className="flex justify-center w-full">
                <img
                  src="/assets/images/BurokDownloadApp.png"
                  alt="Télécharger ShopLink App"
                  className="w-full max-w-md"
                />
              </div>
              
              <p className="text-xs text-gray-500">
                Téléchargez l'application Burok pour vous inscrire et commencer à utiliser ShopLink
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;

